import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Alert } from '@mui/material';
import OvCompactButton from '../atoms/OvCompactButton';
import OvTextButton from '../atoms/OvTextButton';
import OvErrorMessage from '../atoms/OvErrorMessage';
import { VideoPlacement } from '../../../common/model/dto/video/video-placement';
import { getAxiosErrorMessage } from '../../../common/utils/helpers';
import { getHttpStatus } from '../../../common/utils/http-status';
import { useVideoPlacementMutations } from '../../../hooks/use-video-placement-mutations';
import { useVideoPlacementUsage } from '../../../hooks/use-video-placement-usage';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoPlacementDeletePanelProps {
  placement: VideoPlacement;
  onCancel: () => void;
  onShowVideos: (key: string) => void;
}

const OvVideoPlacementDeletePanel: FC<OvVideoPlacementDeletePanelProps> = ({
  placement,
  onCancel,
  onShowVideos,
}) => {
  const { t } = useTranslation();
  const usage = useVideoPlacementUsage(placement.key);
  const { remove } = useVideoPlacementMutations();
  const isConflict = getHttpStatus(remove.error) === 409;

  const showVideosButton = (
    <OvTextButton type="button" onClick={() => onShowVideos(placement.key)}>
      {t('videos.placements.delete.showVideos')}
    </OvTextButton>
  );

  const renderBody = () => {
    if (usage.isPending) {
      return <Note>{t('videos.placements.delete.checking')}</Note>;
    }

    if (usage.isError) {
      return <OvErrorMessage message={getAxiosErrorMessage(usage.error)} />;
    }

    if (usage.data > 0) {
      return (
        <>
          <Alert severity={isConflict ? 'error' : 'warning'}>
            {t(
              isConflict
                ? 'videos.placements.delete.stillUsed'
                : 'videos.placements.delete.used',
              { count: usage.data }
            )}
          </Alert>
          {showVideosButton}
        </>
      );
    }

    if (isConflict) {
      return (
        <>
          <OvErrorMessage message={getAxiosErrorMessage(remove.error)} />
          {showVideosButton}
        </>
      );
    }

    return (
      <>
        <Alert severity="warning">
          {t('videos.placements.delete.unused', { key: placement.key })}
        </Alert>
        {remove.error && (
          <OvErrorMessage message={getAxiosErrorMessage(remove.error)} />
        )}
      </>
    );
  };

  const canDelete = usage.isSuccess && usage.data === 0 && !isConflict;

  return (
    <Panel
      role="group"
      aria-label={t('videos.placements.delete.label', {
        description: placement.description,
      })}
    >
      {renderBody()}
      <Actions>
        <OvCompactButton tone="secondary" onClick={onCancel}>
          {t('common.actions.cancel')}
        </OvCompactButton>
        {canDelete && (
          <OvCompactButton
            tone="danger"
            isBusy={remove.isPending}
            busyLabel={t('videos.placements.delete.busy')}
            onClick={() => remove.mutate(placement.key)}
          >
            {t('videos.placements.delete.submit')}
          </OvCompactButton>
        )}
      </Actions>
    </Panel>
  );
};

export default OvVideoPlacementDeletePanel;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
`;

const Note = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const Actions = styled.div`
  display: flex;
  align-self: flex-end;
  gap: 0.5rem;
`;
